import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { UserDetails } from '@troyai/auth/data-access';
import type { HealthStoryCategory } from '@troyai/portal/health-timeline/data-access';
import {
  demoSettings,
  isDemoMode,
  isPharmacyDemo,
  updateDemoSettings,
} from '@troyai/portal/investor-demo/core';
import {
  Observable,
  catchError,
  defaultIfEmpty,
  delay,
  filter,
  map,
  mergeMap,
  of,
  throwError,
} from 'rxjs';

import { MergeKind, classifyMemberRequest, splitVersionIds } from './demo-classify';
import { emptyRealAnswer, mergeAnswer, storyPageOldest } from './demo-merge';
import {
  accountFrom,
  patchCareManagersResponse,
  patchCarePlanTemplatesResponse,
  patchMeResponse,
  perspectiveFor,
} from './demo-me';
import {
  demoApiPath,
  emptyAnswer,
  isDemoActionPath,
  isDemoMemberPath,
  isMemberResourcePath,
  requestQuery,
  routeDemoRequest,
  scriptedStoryPage,
  storyCategory,
} from './demo-routes';
import { DemoStore } from './demo-store';

/** How long an answer from the store takes, so spinners show for a moment as they would live. */
const ANSWER_DELAY_MS = 120;

/** The key `SelectedPharmacyLocalStorageService` keeps the sidebar's pharmacy under. */
const SELECTED_PHARMACY_KEY = 'global-selected-pharmacy';

/** The pharmacy the sidebar showed last, or null (a first visit, or storage unavailable). */
function selectedPharmacyId(): number | null {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(SELECTED_PHARMACY_KEY) ?? 'null');
    const id =
      typeof stored === 'object' && stored !== null
        ? (stored as { global_id?: unknown }).global_id
        : null;
    return typeof id === 'number' ? id : null;
  } catch {
    return null;
  }
}

/** A copy the caller may keep: the store's own objects must never end up in (frozen) app state. */
function copyOf<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}

/** The `data` of an `{ data }` answer; null for anything else. */
function dataOf(body: unknown): unknown {
  return typeof body === 'object' && body !== null ? (body as { data?: unknown }).data : null;
}

/**
 * The real routes of `members/<demo member>` the store answers for the scripted fallback member,
 * who exists on no backend: what the patient page and the run read. Any other real route of hers
 * is answered empty.
 */
const LOCAL_REAL_ROUTES = new Set([
  '',
  'contact-info',
  'contact-info-history',
  'actions',
  'ongoing-actions',
  'gaps',
  'rx-claims',
  'care-plans',
  'case-notes',
  'intervention-episodes/candidates',
  // The Care Console Timeline reads these on every view; an empty `{}` would break it.
  'changes',
  'change-activity',
]);

/**
 * What a story page's `next_cursor` stands for. The frontend asks for a later page by its cursor
 * alone, so the page's upper bound (the oldest real entry of the page before) and the category the
 * first page was asked with are remembered here, and carried forward from page to page.
 */
interface StoryCursor {
  bound: string | null;
  category: HealthStoryCategory | null;
}

function nextCursorOf(page: unknown): string | null {
  const cursor =
    typeof page === 'object' && page !== null
      ? (page as { next_cursor?: unknown }).next_cursor
      : undefined;
  return typeof cursor === 'string' && cursor !== '' ? cursor : null;
}

/**
 * Serves the demo member from the browser, on top of her real data. Registered first, so it is
 * outermost: what it answers never reaches the other interceptors or the network. With demo mode
 * off it does nothing.
 *
 * In demo mode a request about the demo member (`members/<demo member>/**`) is classified
 * (`classifyMemberRequest`): the scripted objects are answered from the store, most requests go on
 * to the real backend untouched, and the lists that hold both are asked of the backend and
 * merged with the store's. The few other paths that name her go to the backend, and her two linked
 * actions (ids `SCRIPTED_ID_BASE + 9601` and `SCRIPTED_ID_BASE + 9602`) are answered empty. `me`,
 * `cm` and the care plan templates are the real answer with the demo's additions (and `me` also
 * says which perspective runs and, in the pharmacy's, who "you" is); `cm/outreach` refused (403) is
 * answered empty; every other request goes on untouched. With no member saved (the scripted fallback member, who exists on no backend) it
 * answers her requests itself.
 */
@Injectable()
export class DemoBackendInterceptor implements HttpInterceptor {
  private readonly store = inject(DemoStore);
  private readonly storyCursors = new Map<string, StoryCursor>();

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!isDemoMode()) {
      return next.handle(req);
    }
    const path = demoApiPath(req.url);
    if (!path) {
      return next.handle(req);
    }

    // The two linked actions' own page is not part of the demo: answered empty.
    if (isDemoActionPath(path)) {
      return this.answer(req, emptyAnswer(path));
    }

    if (isDemoMemberPath(path)) {
      // With no member saved the demo runs on the scripted fallback member, who exists on no
      // backend: the store answers her requests (the real half of a merge empty; a real route
      // outside the list empty, with the `[demo] local real` warning).
      const local = demoSettings().memberId === null;
      return this.memberRequest(req, next, path, local);
    }
    if (req.method === 'GET' && path.length === 1 && path[0] === 'me') {
      return this.meAnswer(req, next);
    }
    if (req.method === 'GET' && path.length === 1 && path[0] === 'cm') {
      return this.patchResponse(req, next, patchCareManagersResponse);
    }
    if (req.method === 'GET' && path.length === 1 && path[0] === 'care-plan-templates') {
      return this.patchResponse(req, next, patchCarePlanTemplatesResponse);
    }
    if (req.method === 'GET' && path.join('/') === 'cm/outreach') {
      return this.pendingCalls(req, next);
    }
    return next.handle(req);
  }

  /**
   * The signed-in user's calls to log, asked on every patient page by a CM. The care perspective's
   * CM Internal is the frontend's alone (`patchMe`), so the backend refuses an account with no real
   * CM role, such as a Care Team HRA: refused counts as no calls.
   */
  private pendingCalls(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next
      .handle(req)
      .pipe(
        catchError((error: unknown) =>
          error instanceof HttpErrorResponse && error.status === 403
            ? this.answer(req, [])
            : throwError(() => error)
        )
      );
  }

  /**
   * Who answers a request about the demo member: the store, the backend, or both. `local` is true
   * when there is no backend to ask for her: no member saved.
   */
  private memberRequest(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    path: string[],
    local: boolean
  ): Observable<HttpEvent<unknown>> {
    if (!isMemberResourcePath(path)) {
      // The few other paths that name her (her outreach, her adherence table).
      return local ? this.answer(req, this.scriptedData(req, path)) : next.handle(req);
    }
    const rest = path.slice(2);
    const kind = classifyMemberRequest(req.method, rest, requestQuery(req));
    switch (kind.type) {
      case 'scripted':
        return this.scriptedAnswer(req, path);
      case 'real':
        return local ? this.localReal(req, path, rest) : next.handle(req);
      case 'merge':
        return this.merged(req, next, path, kind.merge, local);
    }
  }

  /** The store's answer; a command it refuses, as the backend would, fails as the backend's does. */
  private scriptedAnswer(
    req: HttpRequest<unknown>,
    path: string[]
  ): Observable<HttpEvent<unknown>> {
    try {
      return this.answer(req, this.scriptedData(req, path));
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        return throwError(() => error);
      }
      throw error;
    }
  }

  /**
   * A real route with no backend (no member saved): the store for what the run reads, else empty
   * and a warning.
   */
  private localReal(
    req: HttpRequest<unknown>,
    path: string[],
    rest: string[]
  ): Observable<HttpEvent<unknown>> {
    if (req.method === 'GET' && LOCAL_REAL_ROUTES.has(rest.join('/'))) {
      return this.answer(req, this.scriptedData(req, path));
    }
    // Breaks visibly in the click-through if a scripted command was taken for a real one.
    console.warn('[demo] local real', req.method, req.url);
    return this.answer(req, emptyAnswer(path));
  }

  /** A list of both kinds: her real rows (asked of the backend, none when local) and the scripted. */
  private merged(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    path: string[],
    kind: MergeKind,
    local: boolean
  ): Observable<HttpEvent<unknown>> {
    const query = requestQuery(req);
    const cursor = kind === 'story' ? query('cursor') || null : null;
    const remembered = cursor === null ? undefined : this.storyCursors.get(cursor);
    const bound = remembered?.bound ?? null;
    // A cursor page names no category (the cursor pins it); it also must not go through the story
    // route, which would record "all categories" as the one the +3 days reload re-reads.
    const category =
      cursor === null ? storyCategory(query('category')) : (remembered?.category ?? null);
    const scripted =
      cursor === null ? this.scriptedData(req, path) : scriptedStoryPage(this.store, category);

    // What the real half is when there is none: merged with the scripted half it leaves it as is.
    const none = emptyRealAnswer(kind, scripted);
    const real$ = local ? of(none) : this.realHalf(req, next, kind, none);
    return real$.pipe(
      mergeMap((real) => {
        const following = kind === 'story' ? nextCursorOf(real) : null;
        if (following !== null) {
          this.storyCursors.set(following, { bound: storyPageOldest(real), category });
        }
        return this.answer(req, mergeAnswer(kind, real, scripted, bound));
      })
    );
  }

  /**
   * The backend's answer for a merged list: its `data`, exactly once. A failed request is logged
   * and counts as `none`, so the scripted half is answered alone.
   */
  private realHalf(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    kind: MergeKind,
    none: unknown
  ): Observable<unknown> {
    let request = req;
    if (kind === 'versions') {
      // Only the real ids are the backend's to answer; the scripted ones never leave the browser.
      const { real } = splitVersionIds(requestQuery(req)('ids'));
      if (real.length === 0) {
        return of(none);
      }
      request = req.clone({ params: req.params.set('ids', real.join(',')) });
    }
    return next.handle(request).pipe(
      filter((event): event is HttpResponse<{ data?: unknown }> => event instanceof HttpResponse),
      map((response) => response.body?.data),
      catchError((error: unknown) => {
        console.warn(
          '[demo] real half failed',
          req.method,
          req.url,
          error instanceof HttpErrorResponse ? error.status : undefined
        );
        return of(none);
      })
    );
  }

  /** The store's answer to a request about the demo member, or the empty catch-all. */
  private scriptedData(req: HttpRequest<unknown>, path: string[]): unknown {
    const result = routeDemoRequest(req, this.store);
    if (result.handled) {
      return result.data;
    }
    console.warn('[demo] unhandled', req.method, req.url);
    // A plural-looking last segment is a list; anything else an object.
    return (path[path.length - 1] ?? '').endsWith('s') ? [] : {};
  }

  private answer(req: HttpRequest<unknown>, data: unknown): Observable<HttpEvent<unknown>> {
    // Hidden at the browser's default log level.
    console.debug('[demo] answered', req.method, req.url);
    return of(
      new HttpResponse({
        status: 200,
        url: req.url,
        body: { data: copyOf(data) },
      })
    ).pipe(delay(ANSWER_DELAY_MS));
  }

  /**
   * The real `me`, patched. What it says is remembered first: whose story runs and, in the
   * pharmacy's, who "you" is, which waits for the profile's name.
   */
  private meAnswer(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      mergeMap((event) => {
        if (!(event instanceof HttpResponse)) {
          return of(event);
        }
        this.rememberPerspective(event.body);
        return this.rememberAccount(req, next, event.body).pipe(
          map(() => event.clone({ body: patchMeResponse(event.body) }))
        );
      })
    );
  }

  /**
   * The real `me` decides whose story runs. Stored before the answer is patched, because the
   * patch changes the roles the rule looks for.
   */
  private rememberPerspective(body: unknown): void {
    const data = dataOf(body);
    if (typeof data === 'object' && data !== null) {
      updateDemoSettings({ perspective: perspectiveFor(data as UserDetails) });
    }
  }

  /**
   * Stores who "you" is: the pharmacist's account (see `accountFrom`; `me` has no name, so the
   * profile is asked for it), or none in the care perspective. Emits once, whatever the profile
   * request does.
   */
  private rememberAccount(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    body: unknown
  ): Observable<void> {
    const me = dataOf(body);
    if (typeof me !== 'object' || me === null) {
      return of(undefined);
    }
    if (!isPharmacyDemo()) {
      updateDemoSettings({ account: null });
      return of(undefined);
    }
    // The same request, for the last path segment `profile` in place of `me`.
    const profile = req.clone({ url: req.url.replace(/\/me(?=\/?([?#]|$))/, '/profile') });
    return next.handle(profile).pipe(
      filter((event): event is HttpResponse<{ data?: unknown }> => event instanceof HttpResponse),
      map((response) => response.body?.data),
      catchError(() => of(null)),
      defaultIfEmpty(null),
      map((data) => updateDemoSettings({ account: accountFrom(me, data, selectedPharmacyId()) }))
    );
  }

  /** Lets the real request through and rewrites the body of the answer. */
  private patchResponse(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    patch: (body: unknown) => unknown
  ): Observable<HttpEvent<unknown>> {
    return next
      .handle(req)
      .pipe(
        map((event) =>
          event instanceof HttpResponse ? event.clone({ body: patch(event.body) }) : event
        )
      );
  }
}
